import { useState, useEffect } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/api"
import { Card } from "@/components/ui/Card"
import { Button } from "@/components/ui/Button"
import { ConfirmDialog } from "@/components/ui/ConfirmDialog"
import {
  Linkedin, Mail, Server, Key, CheckCircle, XCircle, Loader2,
  Save, Trash2, TestTube, Eye, EyeOff, Info, ExternalLink, Shield,
} from "lucide-react"

interface ImapConfig {
  imap_host: string | null
  imap_port: number
  imap_username: string | null
  imap_enabled: boolean
  email_integration_mode: string
}

interface OutreachConfig {
  linkedin_email: string | null
  linkedin_profile_url: string | null
  imap: ImapConfig | null
  poll_state: any
}

function useOutreachConfig() {
  return useQuery<OutreachConfig>({
    queryKey: ["outreach-config"],
    queryFn: async () => {
      const { data } = await api.get("/v1/outreach/config")
      return data
    },
  })
}

export default function LinkedInSettings() {
  const qc = useQueryClient()
  const { data: config, isLoading } = useOutreachConfig()

  const [linkedinEmail, setLinkedinEmail] = useState("")
  const [linkedinUrl, setLinkedinUrl] = useState("")
  const [confirmDeactivateImap, setConfirmDeactivateImap] = useState(false)
  const [imapHost, setImapHost] = useState("imap.gmail.com")
  const [imapPort, setImapPort] = useState(993)
  const [imapUsername, setImapUsername] = useState("")
  const [imapPassword, setImapPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)
  const [saved, setSaved] = useState(false)

  // Sync form with loaded config
  useEffect(() => {
    if (config) {
      setLinkedinEmail(config.linkedin_email || "")
      setLinkedinUrl(config.linkedin_profile_url || "")
      setImapHost(config.imap?.imap_host || "imap.gmail.com")
      setImapPort(config.imap?.imap_port || 993)
      setImapUsername(config.imap?.imap_username || "")
    }
  }, [config])

  // Save LinkedIn config
  const saveLinkedin = useMutation({
    mutationFn: async () => {
      await api.put("/v1/outreach/config/linkedin", {
        linkedin_email: linkedinEmail,
        linkedin_profile_url: linkedinUrl,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["outreach-config"] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    },
  })

  // Save IMAP config
  const saveImap = useMutation({
    mutationFn: async () => {
      await api.put("/v1/outreach/config/imap", {
        imap_host: imapHost,
        imap_port: imapPort,
        imap_username: imapUsername,
        imap_password: imapPassword,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["outreach-config"] })
      setImapPassword("")
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    },
  })

  // Test IMAP connection
  const testImap = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ success: boolean; message: string; mailbox_count?: number; linkedin_count?: number }>("/v1/outreach/config/imap/test")
      return data
    },
    onSuccess: (data) => setTestResult(data),
    onError: (err: any) => setTestResult({ success: false, message: err?.response?.data?.detail || "Erro ao testar conexão" }),
  })

  // Delete IMAP config
  const deleteImap = useMutation({
    mutationFn: async () => {
      await api.delete("/v1/outreach/config/imap")
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["outreach-config"] })
      setImapHost("imap.gmail.com")
      setImapPort(993)
      setImapUsername("")
      setImapPassword("")
      setTestResult(null)
    },
  })

  if (isLoading) {
    return <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Carregando...</div>
  }

  const isImapConfigured = config?.imap?.imap_enabled && config?.imap?.imap_host

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-zinc-900 dark:text-white flex items-center gap-2">
          <Linkedin className="h-5 w-5 text-[#0A66C2]" />
          LinkedIn Outreach
        </h3>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
          Configure sua conta LinkedIn para monitorar conexões e gerar leads automaticamente a partir das notificações do LinkedIn.
        </p>
      </div>

      {/* How it works */}
      <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <div className="flex gap-3">
          <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
            <p className="font-semibold">Como funciona</p>
            <p>O sistema monitora sua caixa de entrada via IMAP, identifica notificações do LinkedIn (convites aceitos, mensagens recebidas, InMails) e cria automaticamente leads e eventos no pipeline de Outreach.</p>
            <p className="text-xs text-blue-600 dark:text-blue-300 mt-2">
              <strong>Pré-requisito:</strong> Sua conta Google deve ter a verificação em duas etapas ativa e uma senha de app gerada.
              <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 ml-1 underline">
                Gerar senha de app <ExternalLink className="h-3 w-3" />
              </a>
            </p>
          </div>
        </div>
      </Card>

      {/* Status badge */}
      {isImapConfigured ? (
        <div className="flex items-center gap-2 text-sm">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <span className="text-green-600 dark:text-green-400 font-medium">Monitoramento ativo</span>
          <span className="text-zinc-400">— {config?.imap?.imap_username}</span>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm">
          <XCircle className="h-4 w-4 text-zinc-400" />
          <span className="text-zinc-500 font-medium">Monitoramento não configurado</span>
        </div>
      )}

      {/* LinkedIn Profile */}
      <Card className="p-5 space-y-4">
        <h4 className="font-semibold text-zinc-900 dark:text-white flex items-center gap-2">
          <Linkedin className="h-4 w-4 text-[#0A66C2]" />
          Perfil LinkedIn
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-1.5">
              Email cadastrado no LinkedIn
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
              <input
                type="email"
                value={linkedinEmail}
                onChange={e => setLinkedinEmail(e.target.value)}
                placeholder="seu.email@empresa.com"
                className="w-full pl-10 pr-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-1.5">
              URL do Perfil LinkedIn
            </label>
            <div className="relative">
              <Linkedin className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
              <input
                type="url"
                value={linkedinUrl}
                onChange={e => setLinkedinUrl(e.target.value)}
                placeholder="https://linkedin.com/in/seu-perfil"
                className="w-full pl-10 pr-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={() => saveLinkedin.mutate()} disabled={saveLinkedin.isPending} className="text-xs">
            <Save className="h-3.5 w-3.5 mr-1.5" />
            {saveLinkedin.isPending ? "Salvando..." : "Salvar Perfil"}
          </Button>
          {saved && <span className="text-xs text-green-500 font-medium">Salvo!</span>}
        </div>
      </Card>

      {/* IMAP Configuration */}
      <Card className="p-5 space-y-4">
        <h4 className="font-semibold text-zinc-900 dark:text-white flex items-center gap-2">
          <Server className="h-4 w-4 text-purple-500" />
          Monitoramento de Email (IMAP)
        </h4>
        <p className="text-xs text-zinc-500 dark:text-zinc-400">
          O sistema conecta à sua caixa de entrada via IMAP (somente leitura) para buscar notificações do LinkedIn automaticamente.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-1.5">
              Servidor IMAP
            </label>
            <div className="relative">
              <Server className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
              <input
                type="text"
                value={imapHost}
                onChange={e => setImapHost(e.target.value)}
                placeholder="imap.gmail.com"
                className="w-full pl-10 pr-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <p className="text-[10px] text-zinc-400 mt-1">Google Workspace: imap.gmail.com | Outlook: outlook.office365.com</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-1.5">
              Porta
            </label>
            <input
              type="number"
              value={imapPort}
              onChange={e => setImapPort(Number(e.target.value))}
              className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-[10px] text-zinc-400 mt-1">SSL: 993 (padrão) | STARTTLS: 587</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-1.5">
              Email (login IMAP)
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
              <input
                type="email"
                value={imapUsername}
                onChange={e => setImapUsername(e.target.value)}
                placeholder="seu.email@empresa.com"
                className="w-full pl-10 pr-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-1.5">
              Senha de App
            </label>
            <div className="relative">
              <Key className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
              <input
                type={showPassword ? "text" : "password"}
                value={imapPassword}
                onChange={e => setImapPassword(e.target.value)}
                placeholder={isImapConfigured ? "••••••••••••••••" : "Gere em myaccount.google.com/apppasswords"}
                className="w-full pl-10 pr-10 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-2.5 text-zinc-400 hover:text-zinc-600"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-[10px] text-zinc-400 mt-1">
              <Shield className="inline h-3 w-3 mr-0.5" />
              Armazenada com criptografia. Usada somente para leitura IMAP.{" "}
              <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400 underline">Obtenha a senha aqui</a>
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-zinc-100 dark:border-zinc-800">
          <Button
            onClick={() => saveImap.mutate()}
            disabled={saveImap.isPending || !imapHost || !imapUsername}
            className="text-xs"
          >
            <Save className="h-3.5 w-3.5 mr-1.5" />
            {saveImap.isPending ? "Salvando..." : "Salvar e Ativar"}
          </Button>
          <Button
            variant="outline"
            onClick={() => testImap.mutate()}
            disabled={testImap.isPending || (!isImapConfigured && !imapHost)}
            className="text-xs"
          >
            <TestTube className="h-3.5 w-3.5 mr-1.5" />
            {testImap.isPending ? "Testando..." : "Testar Conexão"}
          </Button>
          {isImapConfigured && (
            <Button
              variant="ghost"
              onClick={() => setConfirmDeactivateImap(true)}
              disabled={deleteImap.isPending}
              className="text-xs text-red-500 hover:text-red-600"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Desativar
            </Button>
          )}
          {saved && <span className="text-xs text-green-500 font-medium">Configuração salva!</span>}
        </div>

        {/* Test result */}
        {testResult && (
          <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${testResult.success ? "bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200" : "bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200"}`}>
            {testResult.success ? <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" /> : <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />}
            <span>{testResult.message}</span>
          </div>
        )}
      </Card>

      {/* Info about what gets monitored */}
      <Card className="p-4">
        <h4 className="font-semibold text-zinc-900 dark:text-white text-sm mb-3">O que é monitorado automaticamente</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {[
            { icon: "🤝", title: "Convites Aceitos", desc: "Novas conexões do LinkedIn geram leads" },
            { icon: "💬", title: "Mensagens Recebidas", desc: "Respostas avançam o status no pipeline" },
            { icon: "📩", title: "InMails", desc: "InMails recebidos são registrados como eventos" },
            { icon: "👀", title: "Visualizações de Perfil", desc: "Quem visualizou seu perfil" },
            { icon: "🔔", title: "Menções e Reações", desc: "Interações em posts e artigos" },
            { icon: "📊", title: "Métricas", desc: "Funil de conversão e taxa de resposta" },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-zinc-50 dark:bg-zinc-800/50">
              <span className="text-lg">{item.icon}</span>
              <div>
                <p className="text-xs font-semibold text-zinc-800 dark:text-zinc-200">{item.title}</p>
                <p className="text-[10px] text-zinc-500 dark:text-zinc-400">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <ConfirmDialog
        open={confirmDeactivateImap}
        title="Desativar monitoramento IMAP?"
        message="A integração com o servidor IMAP será desativada. Você pode reativá-la a qualquer momento."
        confirmLabel="Desativar"
        variant="warning"
        isLoading={deleteImap.isPending}
        onConfirm={async () => {
          await deleteImap.mutateAsync()
          setConfirmDeactivateImap(false)
        }}
        onCancel={() => setConfirmDeactivateImap(false)}
      />
    </div>
  )
}
